import type { AuditableEntity, HistoryActor } from "@/features/types";

export type MessageStatus = "pending" | "handled";

export type MessageUrgency = "low" | "normal" | "high";

/**
 * The three ways a request can be closed, which all answer one question: **who does
 * the follow-up**. The answer itself is not one of them — it lives on its own and is
 * saved without closing anything.
 */
export type MessageResolution =
  | ""
  /** The agent calls the customer back with the team's answer. */
  | "delegated"
  /** Somebody dealt with it themselves. */
  | "handled_by_human"
  /** Nobody is going to do anything, and that is a decision. */
  | "dropped";

/** One follow-up step: proposed by the AI, ticked off by the team. */
export interface MessageTask {
  id: string;
  label: string;
  done: boolean;
  done_at: number;
  done_by: string;
}

export interface ClientMessage extends AuditableEntity {
  id: string;
  client_id: string;
  /** The conversation the request came out of — the first one. */
  session_id: string;
  /**
   * Every conversation that fed the request, oldest first. Missing or empty on
   * requests written before it existed: read it through `messageSessionIds`.
   */
  session_ids?: string[];
  customer_id: string;
  agent_id: string;

  customer_name: string;
  /** The customer's name when it was only heard on a call (shown with a "?"), else "". */
  unsure_customer_name?: string;
  phone_number: string;
  language: string;
  timezone: string;

  subject: string;
  /** Markdown. */
  body: string;
  tasks?: MessageTask[];
  /** A category key (`MessageCategory.key`), kept as filed even once removed. */
  category: string;
  urgency: MessageUrgency;

  assigned_member_id: string;
  assigned_member_name: string;

  /**
   * When the team should have dealt with it: the deadline its urgency earns, or
   * what the customer was told when sooner. 0 on older rows with no promise.
   */
  due_at: number;
  /** Where `due_at` comes from. Empty on older rows, where it was a promise. */
  due_source?: "" | "promised" | "urgency" | "manual";

  source: "agent" | "review";
  status: MessageStatus;

  handled_at: number;
  handled_by: string;
  reply: string;
  /** What the review pass would have answered. Never merged into `reply`. */
  suggested_reply: string;
  resolution: MessageResolution;
  reminder_id: string;
  reply_delivered_at: number;
}

/** A category requests are filed under, as the tenant configured it. */
export interface MessageCategory {
  key: string;
  /** Empty on a built-in category nobody renamed: translated from its key. */
  label: string;
  description: string;
  builtin: boolean;
}

/** The categories a tenant gets without configuring any. */
export const BUILTIN_CATEGORY_KEYS = [
  "callback",
  "complaint",
  "quote",
  "information",
  "other",
] as const;

export const DEFAULT_MESSAGE_CATEGORIES: MessageCategory[] =
  BUILTIN_CATEGORY_KEYS.map((key) => ({
    key,
    label: "",
    description: "",
    builtin: true,
  }));

/** Most urgent first: the order the inbox groups and sorts by. */
export const URGENCY_ORDER: MessageUrgency[] = ["high", "normal", "low"];

/** Was the deadline promised to the customer? Older rows only ever held a promise. */
export function deadlineWasPromised(message: ClientMessage): boolean {
  return message.due_source !== "urgency" && message.due_source !== "manual";
}

/** Where the proposed call back time comes from. */
export type CallbackSource = "customer_preference" | "opening_hours" | "default";

export interface CallbackSlot {
  at: number;
  source: CallbackSource;
  timezone: string;
  /** False when the tenant switched automatic call backs off. */
  enabled: boolean;
}

/** A request is late when its deadline has passed. */
export function messageIsLate(message: ClientMessage, now = Date.now()): boolean {
  return (
    message.status === "pending" && !!message.due_at && message.due_at < now
  );
}

/** Every conversation behind a request, the first one included. */
export function messageSessionIds(
  message: Pick<ClientMessage, "session_id" | "session_ids">,
): string[] {
  return [
    ...new Set(
      [message.session_id, ...(message.session_ids || [])].filter(Boolean),
    ),
  ];
}

/**
 * One line of a request's timeline. A member's comment is **public** (for the
 * customer: the agent tells them on their next call) or **private** (the team only);
 * the agent's `update` after a new call and the system's `event` are the team's.
 */
export interface MessageEntry {
  id: string;
  message_id: string;
  kind: "comment" | "update" | "event";
  visibility: "public" | "private";
  author_type: "member" | "agent" | "system";
  author_id: string;
  author_name: string;
  /** The conversation behind an agent update. */
  session_id: string;
  /** Code of an event: `reopened`, `closed:delegated`, … */
  event: string;
  body: string;
  /** When the agent passed a public comment on, 0 while it is still waiting. */
  delivered_at: number;
  /**
   * When a member deleted the comment, 0 otherwise. The text is gone: what is left is
   * a tombstone, for the team only.
   */
  removed_at?: number;
  removed_by_name?: string;
  created_at: number;
}

export type MessageVisibility = MessageEntry["visibility"];

/** One version of the request itself that changed something (the audit trail). */
export interface MessageHistoryEntry {
  operation: "INSERT" | "UPDATE" | "DELETE";
  operation_timestamp: number;
  changes: Array<
    "status" | "subject" | "body" | "urgency" | "assignee" | "due_at" | "tasks"
  >;
  /** Only what changed in this version. */
  values: {
    status?: string;
    urgency?: MessageUrgency;
    assignee?: string;
    due_at?: number;
  };
  actor: HistoryActor;
}

/** How far back a closed request still counts as "recent" next to the one on screen. */
export const RELATED_CLOSED_DAYS = 30;

/**
 * The other requests of the same contact worth a glance from a request's page:
 * every one still open, and those closed within `RELATED_CLOSED_DAYS`.
 * Open first (most recent first), then closed (most recently closed first).
 */
export function relatedMessages(
  current: Pick<ClientMessage, "id">,
  all: ClientMessage[],
  now = Date.now(),
): { open: ClientMessage[]; closed: ClientMessage[] } {
  const since = now - RELATED_CLOSED_DAYS * 24 * 60 * 60 * 1000;
  const others = all.filter((m) => m.id !== current.id);
  return {
    open: others
      .filter((m) => m.status === "pending")
      .sort((a, b) => b.created_at - a.created_at),
    closed: others
      .filter(
        (m) =>
          m.status === "handled" && (m.handled_at || m.created_at) >= since,
      )
      .sort(
        (a, b) =>
          (b.handled_at || b.created_at) - (a.handled_at || a.created_at),
      ),
  };
}
